import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS,
  ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_LEADERBOARD,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION_LEADERBOARD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { metadatas } from '@sorare/core/src/lib/seo/common';

import { CollectionHeader } from '../CollectionHeader';
import type {
  CollectionLayoutQuery,
  CollectionLayoutQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(FullWidthContainer)`
  background-color: var(--c-black);
  color: var(--c-white);
  min-height: var(--100vh);
`;

const COLLECTION_LAYOUT_QUERY = gql`
  query CollectionLayoutQuery($collection: String!, $slug: String!) {
    user(slug: $slug) {
      slug
      nickname
    }
    cardCollection(slug: $collection) {
      slug
      name
      userCardCollection(forUserSlug: $slug) {
        id
        ...CollectionHeader_userCardCollection
      }
      socialPictureUrls(forUserSlug: $slug) {
        post
        square
        story
      }
      ...CollectionHeader_cardCollection
    }
  }
  ${CollectionHeader.fragments.cardCollection}
  ${CollectionHeader.fragments.userCardCollection}
` as TypedDocumentNode<CollectionLayoutQuery, CollectionLayoutQueryVariables>;

type Props = {
  userSlug: string;
  collection: string;
  fromMyClub?: boolean;
};
export const CollectionLayout = ({
  userSlug,
  collection,
  fromMyClub,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { isMe } = useCurrentUserContext();

  const { data } = useQuery(COLLECTION_LAYOUT_QUERY, {
    variables: {
      collection,
      slug: userSlug,
    },
  });
  const { user, cardCollection } = data || {};
  const userCardCollection = cardCollection?.userCardCollection;

  const canSetMetadata = !!user?.nickname && !!cardCollection?.name;

  useTitleAndDescription(
    metadatas.collection.title,
    undefined,
    canSetMetadata
      ? { username: user?.nickname, collectionName: cardCollection.name }
      : false,
    {
      img:
        cardCollection?.socialPictureUrls?.post ||
        cardCollection?.socialPictureUrls?.square ||
        cardCollection?.socialPictureUrls?.story ||
        undefined,
    }
  );

  const cardsPath = isMe(userSlug)
    ? generateSportContextPath(
        ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS,
        {
          params: {
            collectionSlug: collection,
          },
        }
      )
    : generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION, {
        params: {
          collection,
          slug: userSlug,
        },
      });

  const leaderboardPath = isMe(userSlug)
    ? generateSportContextPath(
        ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_LEADERBOARD,
        {
          params: {
            collectionSlug: collection,
          },
        }
      )
    : generateSportContextPath(
        ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION_LEADERBOARD,
        {
          params: {
            collection,
            slug: userSlug,
          },
        }
      );

  const { team } = cardCollection || {};

  return (
    <>
      <NoIndex />
      {!fromMyClub && <PageHeader text={team?.name} />}
      <CollectionHeader
        userCardCollectionSlug={userSlug}
        userCardCollection={userCardCollection}
        cardCollection={cardCollection}
      />
      <TabBar fullWidth variant="flat">
        <Tab to={cardsPath} matches={[cardsPath]} replace>
          <FormattedMessage {...glossary.cards} />
        </Tab>
        <Tab to={leaderboardPath} matches={[leaderboardPath]} replace>
          <FormattedMessage {...fantasy.leaderboard} />
        </Tab>
      </TabBar>
      <Wrapper>
        <Outlet />
      </Wrapper>
    </>
  );
};
